/*
  gameStringDecodeEncode.ts
  Copyright (c) 2013-2024 Human Cube Inc.

  Game core details decoded/encoded from/into a single string.
*/

import { Game } from './gameTypes';


function intFrom36String (s: string) {
  // Safely convert any value to an integer.
  // 0 for undefined and 0 for NaN.
  // Assumes base 36 string encoding.
  let v = 0;
  if(s) {
    v = parseInt(s, 36);
    if(!v)
      v = 0;
}
return v;
}

export function decodeGameString (gameString: string) {
  // returns an object containing all the decoded game fields from the gameString.
  // returns null on error.

  if(!gameString) {
    return null;
  }

  const parts = gameString.split('|');
  if(parts.length < 18) {
    return null;
  }

  const playerID = [];
  const playerParts = parts[6].split(',');
  for(let i = 0; i < playerParts.length; i++) {
    playerID.push(intFrom36String(playerParts[i]));
  }

  const playerName = parts[7].split(',');

  const team = [];
  const teamParts = parts[9].split(',');
  for(let i = 0; i < teamParts.length; i++) {
    team.push(intFrom36String(teamParts[i]));
  }

  const teamName = parts[10].split(',');

  return {
    _id: intFrom36String(parts[0]),
    flags: intFrom36String(parts[1]),
    name: parts[2],
    scenarioID: intFrom36String(parts[3]),
    mapID: intFrom36String(parts[4]),
    players: intFrom36String(parts[5]),
    playerID: playerID,
    playerName: playerName,
    teams: intFrom36String(parts[8]),
    team: team,
    teamName: teamName,
    state: intFrom36String(parts[11]),
    turn: intFrom36String(parts[12]),
    year: intFrom36String(parts[13]),
    timeLimit: intFrom36String(parts[14]),
    required: intFrom36String(parts[15]),
    noWait: intFrom36String(parts[16]),
    submitted: intFrom36String(parts[17])
  };
}

export function encodeGameString (game: Game): string {

  let playerIDString = '';
  if(Array.isArray(game.playerID)) {
    for(let i = 0; i < game.playerID.length; i++) {
      if(i > 0) {
        playerIDString += ',';
      }
      playerIDString += game.playerID[i].toString(36);
    }
  }
  let playerNameString = '';
  if(Array.isArray(game.playerName)) {
    playerNameString = game.playerName.toString();
  }

  let teamString = '';
  if(Array.isArray(game.team)) {
    for(let i = 0; i < game.team.length; i++) {
      if(i > 0) {
        teamString += ',';
      }
      teamString += game.team[i].toString(36);
    }
  }
  let teamNameString = '';
  if(Array.isArray(game.teamName)) {
    teamNameString = game.teamName.toString();
  }

  let timeLimitMS = 0;
  const timeLimit = new Date(game.timeLimit);
  if(timeLimit.getTime()) {
    timeLimitMS = Math.floor(timeLimit.getTime());
  }

  return (
    game._id.toString(36) + '|' + game.flags.toString(36) + '|' + game.name + '|' +
    game.scenarioID.toString(36) + '|' + game.mapID.toString(36) + '|' +
    game.players.toString(36) + '|' + playerIDString + '|' + playerNameString + '|' +
    game.teams.toString(36) + '|' + teamString + '|' + teamNameString + '|' +
    game.state.toString(36) + '|' + game.turn.toString(36) + '|' +
    game.year.toString(36) + '|' +
    timeLimitMS.toString(36) + '|' +
    game.required.toString(36) + '|' +
    game.noWait.toString(36) + '|' +
    game.submitted.toString(36)
  );
}
